import React, { useCallback, useState } from 'react';
import {
  MenuItem,
  SelectField,
  IconButton,
  SwipeableDrawer,
  Card,
  Box,
  TextField,
  Typography,
  useMediaQuery,
} from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';
import { SORT_BY, PROJECT_STATUS } from 'constants/opportunities';
import { checkMarketingiFrameExists } from 'helpers/marketing';

export default function PreviewOpportunitiesSearchBelt({
  onSortChange,
  onSearchChange,
  onFilterChange,
  searchKeyword,
  sortBy,
  statuses,
  totalCount,
}) {
  const { t } = useTranslation();
  const isMobileWidth = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery(theme => theme.breakpoints.down('md'));
  const isMarketingiFrame = checkMarketingiFrameExists();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const onStatusesChange = useCallback(statuses => onFilterChange({ statuses }), [onFilterChange]);
  const filters = (
    <>
      <SelectField
        label={t('preview.filters.filter')}
        aria-label={t('preview.filters.filter_by_opportunity_status')}
        inlineLabel={!isSmall}
        name="statuses"
        value={statuses}
        onChange={e => onStatusesChange(e.target.value)}
        SelectDisplayProps={{
          'data-cy': 'opportunities-filter-statuses',
          'data-testid': 'opportunities-filter-statuses',
        }}
        sx={{ ml: 'auto' }}
      >
        <MenuItem
          value={PROJECT_STATUS.allOpportunities}
          label={t('opportunities.filters.status_all')}
        />
        <MenuItem value={PROJECT_STATUS.open} label={t('opportunities.filters.status_open')} />
        <MenuItem value={PROJECT_STATUS.closed} label={t('opportunities.filters.status_closed')} />
      </SelectField>
      <SelectField
        onChange={onSortChange}
        value={sortBy}
        inlineLabel={!isSmall}
        label={t('opportunities.filters.sort_by')}
        name="sort"
        sx={{ ml: { sm: 4 } }}
      >
        {statuses !== PROJECT_STATUS.closed && (
          <MenuItem label={t('opportunities.filters.most_recent')} value={SORT_BY.mostRecent} />
        )}
        <MenuItem label={t('opportunities.filters.closing_soon')} value={SORT_BY.closingSoon} />
      </SelectField>
    </>
  );
  return (
    <Card>
      <Box alignItems={isMobileWidth ? 'flex-start' : 'flex-end'} display="flex" px={4} py={3}>
        <Box
          display="flex"
          flexDirection={isMobileWidth ? 'column' : 'row'}
          alignItems={isMobileWidth ? 'flex-start' : 'center'}
          flex={1}
          mr={2}
        >
          <TextField
            name="search"
            onChange={e => onSearchChange(e)}
            value={searchKeyword}
            placeholder={t('opportunities.filters.search')}
            type="search"
            sx={{ width: { xs: '100%', sm: '12rem', md: '17rem' }, mr: 2 }}
          />
          {totalCount !== null && (
            <Typography variant="body3">{t('preview.num_results', { totalCount })}</Typography>
          )}
        </Box>
        {isMobileWidth ? (
          <>
            <IconButton
              iconName="filter"
              variant="outlined"
              aria-label={t('opportunities.filters.show_filters')}
              onClick={() => setIsDrawerOpen(val => !val)}
            />
            <SwipeableDrawer
              anchor="left"
              variant="temporary"
              keepMounted={true}
              hideBackdrop
              open={isDrawerOpen}
              onOpen={() => setIsDrawerOpen(true)}
              onClose={() => setIsDrawerOpen(false)}
              PaperProps={{
                sx: {
                  ...(isMarketingiFrame
                    ? {}
                    : {
                        top: theme => theme.mixins.toolbar.minHeight,
                        height: theme => `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
                      }),
                  padding: 3,
                  '& > div:not(:last-child)': { mb: 3 },
                },
              }}
            >
              <IconButton
                aria-label={t('opportunities.filters.hide_filters')}
                borderless
                iconName="times"
                sx={{ ml: 'auto' }}
                onClick={() => setIsDrawerOpen(false)}
              ></IconButton>
              {filters}
            </SwipeableDrawer>
          </>
        ) : (
          filters
        )}
      </Box>
    </Card>
  );
}
